import { subdivisionUsesDropdown } from "@/lib/address/subdivisions";

export type AddressFormFieldId =
  | "country"
  | "adminArea"
  | "locality"
  | "postalCode"
  | "line1"
  | "line2"
  | "recipientName";

export type AddressFormFieldSpec = {
  id: AddressFormFieldId;
  label: string;
  placeholder?: string;
  required?: boolean;
  /** When true, render as select populated from subdivisionsForCountry. */
  adminAreaSelect?: boolean;
};

export type AddressFormLayout = "us" | "ca" | "mx" | "gb" | "generic";

export function addressFormLayoutForCountry(countryCode: string): AddressFormLayout {
  const c = countryCode.trim().toLowerCase();
  if (c === "us") return "us";
  if (c === "ca") return "ca";
  if (c === "mx") return "mx";
  if (c === "gb" || c === "uk") return "gb";
  return "generic";
}

/** Field order after country is chosen (country-first UX). */
export function addressFormFields(countryCode: string): AddressFormFieldSpec[] {
  const layout = addressFormLayoutForCountry(countryCode);
  const adminSelect = subdivisionUsesDropdown(countryCode);

  if (layout === "us") {
    return [
      { id: "country", label: "Country", required: true },
      {
        id: "adminArea",
        label: "State / territory",
        required: true,
        adminAreaSelect: adminSelect,
      },
      { id: "locality", label: "City", placeholder: "City", required: true },
      {
        id: "postalCode",
        label: "ZIP code",
        placeholder: "12345 or 12345-6789",
        required: true,
      },
      { id: "line1", label: "Street address", placeholder: "Street address", required: true },
      { id: "line2", label: "Apt, suite, unit (optional)", placeholder: "Apt, suite, unit" },
      {
        id: "recipientName",
        label: "Recipient / unit (optional)",
        placeholder: "Name or unit (APO/FPO/DPO)",
      },
    ];
  }

  if (layout === "ca") {
    return [
      { id: "country", label: "Country", required: true },
      {
        id: "adminArea",
        label: "Province / territory",
        required: true,
        adminAreaSelect: adminSelect,
      },
      { id: "locality", label: "City", placeholder: "City", required: true },
      {
        id: "postalCode",
        label: "Postal code",
        placeholder: "A1A 1A1",
        required: true,
      },
      { id: "line1", label: "Street address", placeholder: "Street address", required: true },
      { id: "line2", label: "Apt, suite, unit (optional)", placeholder: "Apt, suite, unit" },
    ];
  }

  if (layout === "mx") {
    return [
      { id: "country", label: "Country", required: true },
      {
        id: "line1",
        label: "Street address",
        placeholder: "Calle y número",
        required: true,
      },
      {
        id: "line2",
        label: "Colonia, ext./int. (optional)",
        placeholder: "Colonia, número exterior o interior",
      },
      {
        id: "locality",
        label: "City / municipio",
        placeholder: "Ciudad o municipio",
        required: true,
      },
      {
        id: "adminArea",
        label: "State",
        required: true,
        adminAreaSelect: adminSelect,
      },
      {
        id: "postalCode",
        label: "Postal code (CP)",
        placeholder: "12345",
        required: true,
      },
    ];
  }

  if (layout === "gb") {
    return [
      { id: "country", label: "Country", required: true },
      {
        id: "postalCode",
        label: "Postcode",
        placeholder: "SW1A 1AA",
        required: true,
      },
      {
        id: "line1",
        label: "Address line 1",
        placeholder: "Building and street",
        required: true,
      },
      {
        id: "line2",
        label: "Address line 2 (optional)",
        placeholder: "Locality or flat details",
      },
      {
        id: "locality",
        label: "Town / city",
        placeholder: "Town or city",
        required: true,
      },
      {
        id: "adminArea",
        label: "County (optional)",
        placeholder: "County",
      },
    ];
  }

  return [
    { id: "country", label: "Country", required: true },
    {
      id: "adminArea",
      label: "State / province / region",
      placeholder: "State, province, or region",
    },
    { id: "locality", label: "City", placeholder: "City" },
    { id: "postalCode", label: "Postal code", placeholder: "Postal code" },
    { id: "line1", label: "Street address", placeholder: "Street address line 1" },
    { id: "line2", label: "Street address line 2", placeholder: "Street address line 2" },
  ];
}

/** US ZIP (optional when empty). */
export const US_POSTAL_PATTERN = /^\d{5}(-\d{4})?$/;

/** Canadian postal (optional when empty). Space optional. */
export const CA_POSTAL_PATTERN = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

/** Mexican código postal (optional when empty). */
export const MX_POSTAL_PATTERN = /^\d{5}$/;

/** UK postcode (optional when empty). Includes GIR 0AA. */
export const GB_POSTAL_PATTERN = /^(GIR\s?0AA|[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2})$/i;

export function normalizeCanadianPostal(value: string): string {
  const raw = value.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (raw.length !== 6) return value.trim().toUpperCase();
  return `${raw.slice(0, 3)} ${raw.slice(3)}`;
}

export function normalizeMexicanPostal(value: string): string {
  return value.replace(/\D/g, "").slice(0, 5);
}

export function normalizeUkPostcode(value: string): string {
  const raw = value.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (raw.length < 5) return value.trim().toUpperCase();
  return `${raw.slice(0, -3)} ${raw.slice(-3)}`;
}

export function validatePostalCode(countryCode: string, postalCode: string): string | null {
  const trimmed = postalCode.trim();
  if (!trimmed) return null;
  const c = countryCode.trim().toLowerCase();
  if (c === "us" && !US_POSTAL_PATTERN.test(trimmed)) {
    return "Enter a valid US ZIP code (12345 or 12345-6789).";
  }
  if (c === "ca" && !CA_POSTAL_PATTERN.test(trimmed)) {
    return "Enter a valid Canadian postal code (A1A 1A1).";
  }
  if (c === "mx" && !MX_POSTAL_PATTERN.test(normalizeMexicanPostal(trimmed))) {
    return "Enter a valid Mexican postal code (5 digits).";
  }
  if ((c === "gb" || c === "uk") && !GB_POSTAL_PATTERN.test(normalizeUkPostcode(trimmed))) {
    return "Enter a valid UK postcode (e.g. SW1A 1AA).";
  }
  return null;
}

export function addressRowHasContent(row: {
  line1?: string | null;
  line2?: string | null;
  locality?: string | null;
  adminArea?: string | null;
  postalCode?: string | null;
  recipientName?: string | null;
}): boolean {
  return Boolean(
    row.line1?.trim() ||
      row.line2?.trim() ||
      row.locality?.trim() ||
      row.adminArea?.trim() ||
      row.postalCode?.trim() ||
      row.recipientName?.trim(),
  );
}
